import React, { useCallback, useMemo, useState } from 'react';
import {
  CallingCodeOption,
  PhoneNumber as PhoneNumberType,
  PhoneNumberInput as BasePhoneNumberInput,
} from 'glints-aries';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { inputTestId, subTestId } from 'src/common/testIds';
import { getCountryWithSGFallback } from 'src/modules/ClientConfig/Selectors';
import {
  getWhatsAppEnabledCallingCodeOptions,
  WhatsAppCallingCodeOption,
} from 'src/modules/Profile/PsychFlatProfilePage/Components/WhatsAppBanner/utils';

import * as S from './WhatsAppNumberInput.sc';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseWhatsAppNumber(
  value: string | null | undefined,
  options: WhatsAppCallingCodeOption[],
  defaultCallingCode: number
): PhoneNumberType {
  if (!value) {
    return { callingCode: defaultCallingCode, significantNumber: null };
  }

  const normalized = value.replace(/[\s\-()]/g, '');
  if (!normalized) {
    return { callingCode: defaultCallingCode, significantNumber: null };
  }

  // Sort by calling code length descending to match longest prefix first (e.g. +886 before +8)
  const sorted = [...options].sort(
    (a, b) => String(b.callingCode).length - String(a.callingCode).length
  );

  for (const opt of sorted) {
    const prefix = `+${opt.callingCode}`;
    if (normalized.startsWith(prefix)) {
      const significant = normalized.slice(prefix.length);
      return {
        callingCode: opt.callingCode,
        significantNumber: significant || null,
      };
    }
  }

  // Fallback: use default calling code and treat the rest as significant number
  const stripped = normalized.replace(/^\+/, '');
  return {
    callingCode: defaultCallingCode,
    significantNumber: stripped || null,
  };
}

function toCallingCodeOptions(
  options: WhatsAppCallingCodeOption[]
): CallingCodeOption[] {
  return options.map((opt) => ({
    label: opt.label,
    callingCode: opt.callingCode,
    isFeatured: true,
  }));
}

const phoneMessages = defineMessages({
  whatsAppNumber: {
    id: 'profile-page.basic-info-modal.whatsapp-number-placeholder',
    defaultMessage: 'WhatsApp Number',
  },
  featuredOptionsLabel: {
    id: 'events.signup.form.phoneNumberInput.featuredOptionsLabel',
    defaultMessage: 'Frequently Used',
  },
  otherOptionsLabel: {
    id: 'events.signup.form.phoneNumberInput.otherOptionsLabel',
    defaultMessage: 'The Rest Of The World',
  },
  callingCodeFilterInputPlaceholder: {
    id: 'events.signup.form.phoneNumberInput.callingCodeFilterInputPlaceholder',
    defaultMessage: 'Type country code or country name',
  },
  callingCodeNoOptionsLabel: {
    id: 'events.signup.form.phoneNumberInput.callingCodeNoOptionsLabel',
    defaultMessage:
      'Sorry, there are no results for country {searchTerm}. Please try again.',
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type WhatsAppNumberInputProps = {
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  name?: string;
  errorMessage?: React.ReactNode;
  disabled?: boolean;
  /** See src/common/testIds.ts. Forwarded to BasePhoneNumberInput. */
  testIdPrefix?: string;
};

export const WhatsAppNumberInput = React.forwardRef<
  HTMLDivElement,
  WhatsAppNumberInputProps
>((props, ref) => {
  const { formatMessage } = useIntl();
  const defaultCountryCode = useSelector(getCountryWithSGFallback);
  const { disabled, value, onChange, onBlur, errorMessage, testIdPrefix } =
    props;

  const whatsAppOptions = useMemo(
    () => getWhatsAppEnabledCallingCodeOptions(),
    []
  );

  const callingCodeOptions = useMemo(
    () => toCallingCodeOptions(whatsAppOptions),
    [whatsAppOptions]
  );

  const defaultCallingCode = useMemo(() => {
    const match = whatsAppOptions.find(
      (opt) => opt.countryCode === defaultCountryCode
    );
    return match?.callingCode ?? whatsAppOptions[0]?.callingCode ?? 62;
  }, [defaultCountryCode, whatsAppOptions]);

  const phoneValue: PhoneNumberType = useMemo(
    () => parseWhatsAppNumber(value, whatsAppOptions, defaultCallingCode),
    [value, whatsAppOptions, defaultCallingCode]
  );

  // Track calling code when significantNumber is empty (value string can't encode it)
  const [fallbackCallingCode, setFallbackCallingCode] = useState(
    () => phoneValue.callingCode
  );

  const activePhoneValue: PhoneNumberType = useMemo(
    () => ({
      callingCode: phoneValue.significantNumber
        ? phoneValue.callingCode
        : fallbackCallingCode,
      significantNumber: phoneValue.significantNumber,
    }),
    [phoneValue, fallbackCallingCode]
  );

  const handleChange = useCallback(
    (newValue: PhoneNumberType) => {
      setFallbackCallingCode(newValue.callingCode);
      if (
        newValue.significantNumber !== null &&
        newValue.significantNumber !== undefined &&
        String(newValue.significantNumber).trim() !== ''
      ) {
        onChange(`+${newValue.callingCode}${newValue.significantNumber}`);
      } else {
        onChange('');
      }
    },
    [onChange]
  );

  return (
    <S.Wrapper ref={ref} data-testid={subTestId(testIdPrefix, 'wrapper')}>
      <BasePhoneNumberInput
        value={activePhoneValue}
        onChange={handleChange}
        onBlur={onBlur}
        callingCodeOptions={callingCodeOptions}
        filterValue=""
        isLoadingCallingCodeOptions={false}
        label={formatMessage(phoneMessages.whatsAppNumber)}
        featuredOptionsLabel={formatMessage(phoneMessages.featuredOptionsLabel)}
        otherOptionsLabel={formatMessage(phoneMessages.otherOptionsLabel)}
        callingCodeFilterInputPlaceholder={formatMessage(
          phoneMessages.callingCodeFilterInputPlaceholder
        )}
        callingCodeNoOptionsLabel={formatMessage(
          phoneMessages.callingCodeNoOptionsLabel,
          { searchTerm: '' }
        )}
        error={errorMessage}
        isDisableCallingCode={disabled}
        {...(disabled && { disabled: true })}
        showFilterInput={false}
        data-testid={inputTestId(testIdPrefix)}
      />
    </S.Wrapper>
  );
});

WhatsAppNumberInput.displayName = 'WhatsAppNumberInput';
